import type { Router } from 'vue-router';
import { z } from 'zod';
import { registerTool } from '#layers/core/app/runtime/utils/agentTools';
import { ToolCallFailure } from '#layers/core/app/runtime/utils/agentToolsRegistry';
import { agentEntityNavigation, awaitAgentEntityOutcome } from '#layers/core/app/runtime/utils/agentEntityLoad';

class LocatorHelper implements Locator
{
    private router: Router;
    private sitemap: Sitemap;
    private translate: (key: string) => string;

    public constructor(router: Router, sitemap: Sitemap, translate: (key: string) => string)
    {
        this.router = router;
        this.sitemap = sitemap;
        this.translate = translate;
    }

    // Konwencja routingu plikowego Nuxt: `[[id]]` -> `:id?` (opcjonalny), `[id]`/`[id]()` -> `:id()` (wymagany)
    private requiredParams(path: string)
    {
        return [...path.matchAll(/:(\w+)\(\)/g)].map(match => match[1]);
    }

    private flatten(nodes: SitemapNode[], acc: SitemapNode[] = [])
    {
        for (const node of nodes)
        {
            acc.push(node);

            if (node.children)
            {
                this.flatten(node.children, acc);
            }
        }

        return acc;
    }

    // Uprawnienia do trasy encji rozstrzyga mapa serwisu, nie sam rejestr tras routera. Dla tras
    // takich jak `studio-docs-edit` jedna nazwa trasy ma wiele węzłów (po jednym na moduł/sekcję
    // Dynamic Studio), więc dopasowanie musi uwzględnić routeParams węzła, nie tylko nazwę trasy
    private permitted(nodes: SitemapNode[], routeName: string, params: Record<string, string>)
    {
        const matches = nodes.filter(node => node.route === routeName);

        if (matches.length === 0)
        {
            return false;
        }

        const withParams = matches.filter(node => node.routeParams);

        if (withParams.length === 0)
        {
            return matches.every(node => node.allowed !== false);
        }

        const exact = withParams.find(node => Object.keys(node.routeParams).every(key => node.routeParams[key] === params[key]));

        return exact ? exact.allowed !== false : false;
    }

    private fullPath(node: SitemapNode)
    {
        const parts: string[] = [];
        let current = node;

        while (current)
        {
            parts.unshift(this.translate(current.name));
            current = current.parent;
        }

        return parts.join(' / ');
    }

    // Belka menu podpisuje węzeł najwyższego poziomu nazwą skróconą (`short`) i użytkownik prosi
    // o widok właśnie nią - nazwy pełnej nigdzie nie widzi. Bez uwzględnienia skróconej zapytanie
    // albo nie trafia nic (`Pliki` wobec `Repozytorium plików`), albo trafia obcy węzeł mający je
    // w swojej ścieżce, a wtedy wynik wygląda na powodzenie, tylko na innym ekranie
    private pathLabels(node: SitemapNode)
    {
        const labels = [this.fullPath(node)];

        if (node.short)
        {
            labels.push(this.translate(node.short));
        }

        return labels.map(label => label.toLowerCase());
    }

    private nameLabels(node: SitemapNode)
    {
        const labels = [this.translate(node.name)];

        if (node.short)
        {
            labels.push(this.translate(node.short));
        }

        return labels.map(label => label.toLowerCase());
    }

    // Poziom w sitemapie porządkuje kandydatów pokazywanych wołającemu, a poziom zerowy rozstrzyga
    // remis dokładnych dopasowań. Tylko zerowy, bo tam stoją pozycje belki menu - te, które
    // użytkownik czyta i którymi prosi o widok. Między samymi węzłami zagnieżdżonymi płytszy nie
    // jest z niczego lepszy, więc wybór tam należy do wołającego
    private depth(node: SitemapNode)
    {
        let depth = 0;
        let current = node.parent;

        while (current)
        {
            depth++;
            current = current.parent;
        }

        return depth;
    }

    // Nagłówek sekcji menu (`header: true`) nie ma ani trasy, ani adresu, ani dzieci, a
    // `$sitemap.url()` zwraca dla takiego węzła bieżący adres. Bez tego sprawdzenia wynik
    // wyglądałby na powodzenie, a widok by się nie zmienił. Grupa z widocznym dzieckiem zostaje,
    // bo mapa serwisu schodzi z niej do pierwszego dostępnego dziecka
    private targetable(node: SitemapNode)
    {
        if (node.route || node.url)
        {
            return true;
        }

        return (node.children || []).some(child => !child.header && child.visible);
    }

    private locationOf(node: SitemapNode): LocatorResult
    {
        return {
            ok: true,
            location: this.sitemap.url(node, this.router.currentRoute.value)
        };
    }

    // Kandydat musi nieść gotowy cel, bo wołający buduje z niego odnośnik
    private candidates(nodes: SitemapNode[]): LocatorCandidate[]
    {
        return nodes.map((node) =>
        {
            const location = this.sitemap.url(node, this.router.currentRoute.value);

            return { name: this.fullPath(node), path: this.router.resolve(location).fullPath };
        });
    }

    private failure(reason: LocatorFailureReason, message: string, extra: Partial<LocatorResult> = {}): LocatorResult
    {
        return { ok: false, reason, message, ...extra };
    }

    public async locateEntity(model: string, id: string, params: Record<string, string> = {}): Promise<LocatorResult>
    {
        const route = this.router.getRoutes().find(r => r.meta?.model === model);

        if (!route)
        {
            return this.failure('unknown-model', `Brak trasy panelu dla modelu "${model}".`);
        }

        const routeName = route.name.toString();
        const routeParams = { id, ...params };
        const missing = this.requiredParams(route.path).filter(name => !routeParams[name]);

        if (missing.length > 0)
        {
            return this.failure(
                'missing-param',
                `Trasa "${routeName}" wymaga parametrów: ${missing.join(', ')}.`,
                { pattern: route.path }
            );
        }

        const nodes = this.flatten(await this.sitemap.all());

        if (!this.permitted(nodes, routeName, routeParams))
        {
            return this.failure('forbidden', `Brak uprawnień do widoku encji modelu "${model}".`);
        }

        const location = { name: routeName, params: routeParams };

        // Rozwiązanie po nazwie trasy zawsze przypisuje parametry poprawnie - zawodzi dopiero
        // realny adres. Gdy brakuje parametru opcjonalnego stojącego przed `id` (np. `mode`),
        // zbudowana ścieżka jest krótsza, a jej ponowne rozwiązanie jako zwykłego adresu dopasowuje
        // segmenty pozycyjnie i przesuwa `id` na miejsce tamtego parametru. Porównanie z tym, co
        // podał wołający, wychwytuje to niezależnie od tego, ile parametrów opcjonalnych ma trasa
        // i w jakiej kolejności - bez zgadywania z samego kształtu wzorca
        const resolvedPath = this.router.resolve(location).fullPath;

        // Rozwiązanie zwraca parametry jako tekst, a wołający może podać identyfikator liczbą -
        // bez zrównania stron porównanie odrzucałoby poprawnie zbudowany adres
        if (String(this.router.resolve(resolvedPath).params.id) !== String(id))
        {
            return this.failure(
                'missing-param',
                `Trasa "${routeName}" z podanymi parametrami nie prowadzi do encji o identyfikatorze "${id}".`,
                { pattern: route.path }
            );
        }

        return {
            ok: true,
            location
        };
    }

    public async locateView(name: string): Promise<LocatorResult>
    {
        const query = (name ?? '').trim();

        if (!query)
        {
            return this.failure('name-not-found', 'Nie podano nazwy widoku.');
        }

        const needle = query.toLowerCase();
        const nodes = this.flatten(await this.sitemap.all()).filter(node => node.visible && node.allowed);
        const matched = nodes.filter(node => this.pathLabels(node).some(label => label.includes(needle)));
        const hits = matched.filter(node => this.targetable(node));

        if (hits.length === 0)
        {
            if (matched.length > 0)
            {
                return this.failure('name-not-found', `Nazwa "${name}" wskazuje nagłówek grupy w menu, a nie widok.`);
            }

            return this.failure('name-not-found', `Nie znaleziono widoku o nazwie "${name}".`);
        }

        if (hits.length === 1)
        {
            return this.locationOf(hits[0]);
        }

        // Wiele trafień: najpierw dokładne dopasowanie końcowego segmentu nazwy
        const lastSegment = query.split('/').map(part => part.trim()).pop().toLowerCase();
        const exact = hits.filter(node => this.nameLabels(node).includes(lastSegment));

        if (exact.length === 1)
        {
            return this.locationOf(exact[0]);
        }

        // Remis dokładnych dopasowań rozstrzyga jedna pozycja belki menu, i tylko ona. Sama mniejsza
        // głębokość nie jest przewagą: `Ustawienia` powtarza się na poziomie pierwszym i drugim,
        // a płytsza z tych grup nie ma własnej trasy, więc wybór jej prowadziłby po cichu na ekran
        // zejścia z grupy zamiast na ten, o który prosił użytkownik. Dopasowania częściowe
        // zostawiamy wołającemu z tego samego powodu - stąd warunek na samych dokładnych
        if (exact.length > 1)
        {
            const topLevel = exact.filter(node => this.depth(node) === 0);

            if (topLevel.length === 1)
            {
                return this.locationOf(topLevel[0]);
            }
        }

        // Kandydaci idą od najpłytszego: kolejność z przechodzenia drzewa stawiałaby na początku
        // węzeł zagnieżdżony, a wołający pokazuje tę listę do wyboru. To jedyne miejsce, w którym
        // kolejność ma znaczenie - rozstrzyganie wyżej patrzy na samą głębokość, nie na porządek
        const ranked = (exact.length > 0 ? exact : hits).toSorted((a, b) => this.depth(a) - this.depth(b));

        return this.failure(
            'multiple-matches',
            `Nazwa "${name}" wskazuje więcej niż jeden widok.`,
            { candidates: this.candidates(ranked) }
        );
    }

    public async openEntity(model: string, id: string, params: Record<string, string> = {}): Promise<LocatorResult>
    {
        const result = await this.locateEntity(model, id, params);

        if (result.ok)
        {
            await this.router.push(result.location);
        }

        return result;
    }

    public async openView(name: string): Promise<LocatorResult>
    {
        const result = await this.locateView(name);

        if (result.ok)
        {
            await this.router.push(result.location);
        }

        return result;
    }
}

const gotoViewSchema = z.object({ name: z.string() });
// Identyfikator przychodzi z JSON-a liczbą tak samo naturalnie jak tekstem, a usunięta metoda
// `openEntity` przyjmowała obie postacie - `transform(String)` zrównuje je przed handlerem, bo
// rejestr podaje handlerowi wartość po walidacji. Nie `z.coerce.string()`: ta przepuszcza obiekt
// i oddaje tekst `[object Object]`
// Ten sam powód obejmuje wartości w `params`: usunięty publiczny kontrakt nie walidował argumentów
// wcale, więc zawężenie ich do napisów byłoby cichą zmianą zachowania
const gotoEntitySchema = z.object({
    model: z.string(),
    id: z.union([z.string(), z.number()]).transform(String),
    params: z.record(z.union([z.string(), z.number()]).transform(String)).optional()
});
// `optional()` jest konieczne, bo rejestr podaje do `safeParse` surowe `args`, a `call(name)` bez
// drugiego argumentu daje `undefined`, którego samo `z.object({})` nie przyjmuje - bez tego
// narzędzie opisane w `list()` jako bezargumentowe dałoby się wywołać wyłącznie przez
// `call('goto.routes', {})`
const gotoRoutesSchema = z.object({}).optional();

interface GotoRouteEntry
{
    name: string;
    model?: string;
    pattern: string;
}

interface GotoToolData
{
    location?: LocatorResult['location'];
    pattern?: string;
    candidates?: LocatorCandidate[];
}

// Rozpakowuje `LocatorResult` na potrzeby call() - powodzenie oddaje wprost jako dane handlera,
// niepowodzenie zgłasza przez ToolCallFailure. Bez tego call() zwróciłby własną kopertę (ok: true)
// z zagnieżdżoną kopertą `LocatorResult` w polu `data`, a jej domenowy `reason`
// (unknown-model, forbidden...) nie mieściłby się w zamkniętym zestawie powodów samego rejestru
function unwrapLocatorResult(result: LocatorResult): GotoToolData
{
    if (!result.ok)
    {
        const data: GotoToolData = {};

        if (result.pattern !== undefined)
        {
            data.pattern = result.pattern;
        }

        if (result.candidates !== undefined)
        {
            data.candidates = result.candidates;
        }

        throw new ToolCallFailure(result.reason as LocatorFailureReason, result.message as string, Object.keys(data).length > 0 ? data : undefined);
    }

    return { location: result.location };
}

// `router.getRoutes()` było dotąd czytane wprost z pola `router` helpera (`private` w TypeScript
// jest kompilacyjne, nie chroni w czasie działania) - to jawne narzędzie zastępuje ten dostęp bez
// poszerzania API helpera. Bez filtrowania po uprawnieniach: tabela tras jest kodem, nie danymi
// tenanta, a wejście w widok i tak sprawdza `goto.view`/`goto.entity`
function gotoRoutes(router: Router): GotoRouteEntry[]
{
    return router.getRoutes()
        .filter(route => typeof route.name === 'string')
        .map(route => ({
            name: route.name as string,
            model: typeof route.meta?.model === 'string' ? route.meta.model : undefined,
            pattern: route.path
        }));
}

function registerGotoTools(router: Router, helper: LocatorHelper): void
{
    registerTool(
        'goto.view',
        'Przechodzi do widoku panelu wskazanego nazwą z menu.',
        gotoViewSchema,
        async (args) => unwrapLocatorResult(await helper.openView((args as z.infer<typeof gotoViewSchema>).name)),
        { level: 'global', argumentsDescription: 'name: string - nazwa widoku z menu panelu' }
    );

    registerTool(
        'goto.entity',
        'Przechodzi do widoku encji wskazanej modelem backendowym i identyfikatorem.',
        gotoEntitySchema,
        async (args) =>
        {
            const { model, id, params } = args as z.infer<typeof gotoEntitySchema>;
            const before = agentEntityNavigation();
            const result = unwrapLocatorResult(await helper.openEntity(model, id, params ?? {}));

            // Czekanie w narzędziu, a nie w `openEntity`: helper jest wystawiony jako `$locator`, a jego
            // przejście ma się kończyć razem z nawigacją, jak dotąd
            await awaitAgentEntityOutcome(before, id, 'goto.entity', result as Record<string, unknown>);

            return result;
        },
        { level: 'global', argumentsDescription: 'model: string, id: string | number, params?: Record<string, string | number>' }
    );

    registerTool(
        'goto.routes',
        'Zwraca tabelę tras panelu: nazwę trasy, model backendowy (jeśli trasa go niesie) i wzorzec ścieżki.',
        gotoRoutesSchema,
        () => gotoRoutes(router),
        { level: 'global', argumentsDescription: 'brak' }
    );
}

export default defineNuxtPlugin({
    name: 'locator',
    dependsOn: ['nuxt:router', 'i18n', 'sitemap', 'tools'],
    async setup()
    {
        const { $router, $t, $sitemap } = useNuxtApp();
        const helper = new LocatorHelper($router, $sitemap, $t);

        // Rejestr podaje modułowi rejestracji plugin `tools` z `dependsOn`, więc nie ma już czego
        // bramkować obecnością DOM - bramka odcinałaby rejestrację po cichu, a brak rejestru ma się
        // zgłosić błędem z modułu
        registerGotoTools($router, helper);

        return {
            provide: {
                locator: helper as Locator
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $locator: Locator;
    }
}
